// Stripe-supported presentment currencies for the platform's supported account countries.
// Source: https://docs.stripe.com/currencies#presentment-currencies
export const STRIPE_SUPPORTED_PRESENTMENT_CURRENCIES = [
  "USD",
  "AED",
  "AFN",
  "ALL",
  "AMD",
  "ANG",
  "AOA",
  "ARS",
  "AUD",
  "AWG",
  "AZN",
  "BAM",
  "BBD",
  "BDT",
  "BIF",
  "BMD",
  "BND",
  "BOB",
  "BRL",
  "BSD",
  "BWP",
  "BYN",
  "BZD",
  "CAD",
  "CDF",
  "CHF",
  "CLP",
  "CNY",
  "COP",
  "CRC",
  "CVE",
  "CZK",
  "DJF",
  "DKK",
  "DOP",
  "DZD",
  "EGP",
  "ETB",
  "EUR",
  "FJD",
  "FKP",
  "GBP",
  "GEL",
  "GIP",
  "GMD",
  "GNF",
  "GTQ",
  "GYD",
  "HKD",
  "HNL",
  "HTG",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "ISK",
  "JMD",
  "JPY",
  "KES",
  "KGS",
  "KHR",
  "KMF",
  "KRW",
  "KYD",
  "KZT",
  "LAK",
  "LBP",
  "LKR",
  "LRD",
  "LSL",
  "MAD",
  "MDL",
  "MGA",
  "MKD",
  "MMK",
  "MNT",
  "MOP",
  "MUR",
  "MVR",
  "MWK",
  "MXN",
  "MYR",
  "MZN",
  "NAD",
  "NGN",
  "NIO",
  "NOK",
  "NPR",
  "NZD",
  "PAB",
  "PEN",
  "PGK",
  "PHP",
  "PKR",
  "PLN",
  "PYG",
  "QAR",
  "RON",
  "RSD",
  "RUB",
  "RWF",
  "SAR",
  "SBD",
  "SCR",
  "SEK",
  "SGD",
  "SHP",
  "SLE",
  "SOS",
  "SRD",
  "STD",
  "SZL",
  "THB",
  "TJS",
  "TOP",
  "TRY",
  "TTD",
  "TWD",
  "TZS",
  "UAH",
  "UGX",
  "UYU",
  "UZS",
  "VND",
  "VUV",
  "WST",
  "XAF",
  "XCD",
  "XCG",
  "XOF",
  "XPF",
  "YER",
  "ZAR",
  "ZMW",
] as const;

export type StripeSupportedPresentmentCurrency =
  (typeof STRIPE_SUPPORTED_PRESENTMENT_CURRENCIES)[number];

// Stripe represents these currencies without a fractional minor unit. ISK,
// UGX, and HUF intentionally are not in this list: Stripe charge amounts use
// two decimal places for them even though Intl/ISO formatting commonly uses
// zero. ISK and UGX charge amounts must additionally end in `00`.
export const STRIPE_ZERO_DECIMAL_CURRENCIES = [
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
] as const;

const stripeZeroDecimalCurrencySet = new Set<string>(
  STRIPE_ZERO_DECIMAL_CURRENCIES
);

export const getStripeMinorUnitMultiplier = (currency: string): number =>
  stripeZeroDecimalCurrencySet.has(currency.trim().toUpperCase()) ? 1 : 100;

export const getStripeCurrencyFractionDigits = (currency: string): number =>
  getStripeMinorUnitMultiplier(currency) === 1 ? 0 : 2;

const stripeWholeUnitWithTwoDecimalApiSet = new Set(["ISK", "UGX"]);

/** Smallest increment Stripe accepts for a charge, expressed in API minor units. */
export const getStripeChargeAmountQuantum = (currency: string): number =>
  stripeWholeUnitWithTwoDecimalApiSet.has(currency.trim().toUpperCase())
    ? 100
    : 1;

export const formatStripeMoney = (
  amount: number,
  currency: string,
  locale?: string,
  currencyExponent?: number
): string => {
  const normalizedCurrency = currency.trim().toUpperCase() || "EUR";
  const fractionDigits =
    Number.isInteger(currencyExponent) &&
    (currencyExponent as number) >= 0 &&
    (currencyExponent as number) <= 3
      ? (currencyExponent as number)
      : getStripeCurrencyFractionDigits(normalizedCurrency);

  try {
    return new Intl.NumberFormat(locale, {
      currency: normalizedCurrency,
      style: "currency",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${amount.toFixed(fractionDigits)} ${normalizedCurrency}`;
  }
};

const stripeSupportedPresentmentCurrencySet = new Set<string>(
  STRIPE_SUPPORTED_PRESENTMENT_CURRENCIES
);

export const isStripeSupportedPresentmentCurrency = (
  value: string | null | undefined
): value is StripeSupportedPresentmentCurrency => {
  return stripeSupportedPresentmentCurrencySet.has(value?.trim().toUpperCase() || "");
};

export const getStripeCurrencyDisplayLabel = (
  currency: StripeSupportedPresentmentCurrency,
  locale: string
): string => {
  try {
    const currencyName = new Intl.DisplayNames([locale], { type: "currency" }).of(
      currency
    );
    return currencyName && currencyName !== currency
      ? `${currency} — ${currencyName}`
      : currency;
  } catch {
    return currency;
  }
};
